import { isAfter, isSameDay } from "~/utils/datetime"
import { formatNumber } from "~/utils/formatting"
import { findLastIndex, round } from "lodash"
import {
  getForecastsForWeeks,
  getWeekOfHittingThreshold,
  padAndSumCumulativeArrays,
} from "~/utils/forecastUtils"
import {
  formatLifecycleStatus,
  isLifecycleInactive,
  isLifecyclePhasedOut,
} from "~/utils/lifecycleUtils"
import { stockStatusColor, stockStatusText } from "~/utils/outOfStockDatesUtils"
import { filterDefined, sortByWeekDiff, sumBy } from "~/utils/tsUtils"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { StatusColor } from "~/constants/status"

export type WeeksOnHandExplanation = {
  badge: { text: string; color: StatusColor }
  message: string
}

const NO_DEMAND_MESSAGE = "No demand, so weeks on hand can't be calculated."

// Labels come from formatLifecycleStatus so they cannot drift from the Lifecycle column.
function noDemandBadge(
  entries: Pick<IVariantSummary, "settings">[],
): WeeksOnHandExplanation["badge"] {
  if (isLifecyclePhasedOut(entries))
    return { text: formatLifecycleStatus("phasedOut"), color: StatusColor.Error }
  if (isLifecycleInactive(entries))
    return { text: formatLifecycleStatus("phasingOut"), color: StatusColor.Warning }
  return { text: "No demand", color: StatusColor.Neutral }
}

// Explains a weeks-on-hand cell that cannot show a plain number. `values` is one entry per row:
// a single value for a variant, one per leaf for a group. A group's number is the min–max of its
// calculable leaves only, so unknown leaves are absent from it — hence "partly unknown".
export function getWeeksOnHandExplanation(
  entries: Pick<IVariantSummary, "settings" | "stockLevel">[],
  values: number[],
): WeeksOnHandExplanation | undefined {
  if (entries.length === 0 || values.length === 0) return undefined

  const unknown = values.filter((v) => !isFinite(v)).length
  if (unknown === values.length)
    return { badge: noDemandBadge(entries), message: NO_DEMAND_MESSAGE }
  if (unknown > 0)
    return {
      badge: { text: "Partly unknown", color: StatusColor.Neutral },
      message:
        unknown === 1
          ? `1 of ${values.length} variants has no demand and is excluded from this range.`
          : `${unknown} of ${values.length} variants have no demand and are excluded from this range.`,
    }
  if (entries.every((entry) => entry.stockLevel <= 0))
    return {
      badge: { text: stockStatusText("stockout"), color: stockStatusColor("stockout") },
      message: entries.length > 1 ? "Stock level is 0 across all variants." : "Stock level is 0.",
    }
  return undefined
}

/** A ceiling, not a measurement: past the curve's end the crossing extrapolates. Rendered "52+". */
export const WEEKS_ON_HAND_CAP = 52

export const clampWeeksOnHand = (weeks: number) => Math.min(WEEKS_ON_HAND_CAP, Math.max(0, weeks))

export function calculateWeeksOnHand(
  forecasts: number[] | undefined,
  stockLevel: number,
  weekIdx: number,
) {
  if (stockLevel <= 0) return 0
  // Grid rows can transiently lack forecast data; signal "unknown" via NaN so callers'
  // existing isFinite/!= null checks render "N/A" instead of crashing on forecasts.length.
  if (!forecasts?.length) return NaN
  return (
    getWeekOfHittingThreshold(stockLevel + getForecastsForWeeks(forecasts, weekIdx), forecasts) -
    weekIdx
  )
}

/** `calculateWeeksOnHand`'s display convention: infinite or capped weeks read as "52+", else one decimal. */
export function formatWeeksOnHand(weeks: number): string {
  return !isFinite(weeks) || weeks >= WEEKS_ON_HAND_CAP
    ? `${WEEKS_ON_HAND_CAP}+`
    : formatNumber(weeks, 1)
}

type ProjectedWeeksOnHandInput = {
  /**
   * Projected stock at the expected arrival date, INCLUDING the planned order quantity, taken
   * from the same simulation as `stockoutWeeks` (`StockOutResult.stockLevelAtArrival`). A second,
   * independently projected stock figure can disagree with the times and defeat the gate below.
   */
  stockAtArrival: number
  /**
   * Forecasted demand for the purchase period (arrival -> next arrival), summed over the same
   * warehouses whose demand the simulation consumes — see `poolAddedDemandWarehouses`.
   */
  forecastInPeriod: number
  /**
   * Simulated stock-out times in weeks, one per replenishment segment, ascending
   * (`StockOutResult.stockoutWeeks`). Never the `stockoutDates` equivalents: those are truncated to
   * whole days, so a run-out hours after the order lands reads as "at arrival" and the metric then
   * reports the runway of a later replenishment (HAK-2359).
   */
  stockoutWeeks: number[]
  /** The arrival week in the same scale (`StockOutResult.arrivalWeek`). */
  arrivalWeek: number
}

/**
 * Weeks of cover from the expected arrival date, for the PO table and the NOOS buy table.
 *
 * `stockoutWeeks` holds one time per replenishment segment, so "first one after arrival" alone
 * is not monotonic in the planned quantity: dropping the quantity to 0 removes that segment and
 * the next segment's time — belonging to a later, already-placed order — is picked up instead,
 * counting the whole zero-stock gap as cover (HAK-2359). Anchoring on the projected stock at
 * arrival keeps the metric monotonic: nothing on hand when the order lands means no cover.
 *
 * Returns 0 when nothing is on hand at arrival, `Infinity` when stock never depletes (callers
 * render "52+") and `null` when the window carries no demand — cover is then undefined, not zero
 * (same reading as `NO_DEMAND_MESSAGE` on the product page), and callers render it as no demand
 * so a seasonal SKU cannot drag group averages towards 0.
 */
export function calculateProjectedWeeksOnHand({
  stockAtArrival,
  forecastInPeriod,
  stockoutWeeks,
  arrivalWeek,
}: ProjectedWeeksOnHandInput): number | null {
  if (forecastInPeriod <= 0) return null
  if (stockAtArrival <= 0) return 0
  const weekAfterArrival = stockoutWeeks.find((week) => week > arrivalWeek)
  if (weekAfterArrival === undefined) return Infinity
  return weekAfterArrival - arrivalWeek
}

/**
 * Average weekly demand over the window the stock actually covers — the basis the runway is
 * computed on, so a displayed "units/wk" reconciles with it. Averaging over the whole purchase
 * period does not, because the forecast curve is rarely flat (HAK-2359: 9 units at a 2 u/wk
 * period average, but 4.7 u/wk in the two weeks right after arrival, hence 1.9 weeks of cover).
 * Cover that is unbounded or undefined has no window, so the purchase period stands in.
 *
 * `demandForWeeks(weeks)` must return forecast units for that many weeks from the arrival date.
 * Forecast sources bucket by week, so the window is rounded up to whole weeks and the average
 * uses the same whole weeks — otherwise a 1.9-week window divides two weeks of demand by 1.9.
 * The bucket identity only holds exactly for a week-aligned arrival date; off-Monday arrivals
 * interpolate across buckets (`getForecastsForWeeks`).
 */
export function weeklyDemandDuringCover({
  weeksOnHand,
  weeksInPeriod,
  demandForWeeks,
}: {
  weeksOnHand: number | null
  weeksInPeriod: number
  demandForWeeks: (weeks: number) => number
}): number {
  const measured = weeksOnHand != null && Number.isFinite(weeksOnHand) ? weeksOnHand : weeksInPeriod
  const coverWeeks = Math.max(Math.ceil(measured), 1)
  return demandForWeeks(coverWeeks) / coverWeeks
}

/**
 * Whether stock is projected to run out at or before the order arrives. Independent of any later
 * stockout: a segment that runs dry after arrival does not undo the gap before it (HAK-2359).
 * Compared in weeks, on the same scale as the runway, so the warning and the number always agree
 * on which side of the arrival a stock-out falls.
 */
export function isStockoutBeforeArrival(
  stockoutWeeks: number[],
  arrivalWeek: number,
  forecastInPeriod: number,
): boolean {
  if (forecastInPeriod <= 0) return false
  return stockoutWeeks.some((week) => week <= arrivalWeek)
}

/**
 * The stock-out that ends the reported runway, so the hover card cannot print a date from a
 * different replenishment segment than the headline number (HAK-2359: 0 weeks of cover next to
 * the stock-out date of an order arriving a year later).
 *
 * Picked in week space and read out of the index-aligned `stockoutDates`, which stay the display
 * format (whole days).
 */
export function coverEndStockOutDate(
  { stockoutWeeks, stockoutDates }: { stockoutWeeks: number[]; stockoutDates: Date[] },
  arrivalWeek: number,
  weeksOnHand: number | null,
): Date | undefined {
  if (weeksOnHand == null || !Number.isFinite(weeksOnHand)) return undefined
  const index =
    weeksOnHand > 0
      ? stockoutWeeks.findIndex((week) => week > arrivalWeek)
      : // No cover: the run-out already happened, so point at it rather than at a later segment.
        findLastIndex(stockoutWeeks, (week) => week <= arrivalWeek)
  return index === -1 ? undefined : stockoutDates[index]
}

export type ProjectedCover = {
  /** `Infinity` = never depletes ("52+"), `null` = no demand in the window. */
  weeksOnHand: number | null
  stockOutDate: Date | undefined
  stockoutBeforeArrival: boolean
  /** The stock the cover was measured from, so cards can show a figure the number follows from. */
  stockAtArrival: number
}

/**
 * The whole projected-cover state from one set of inputs, so the number, its stock-out date and
 * the pre-arrival warning cannot disagree with each other.
 */
export function calculateProjectedCover(
  input: ProjectedWeeksOnHandInput & { stockoutDates: Date[] },
): ProjectedCover {
  const weeksOnHand = calculateProjectedWeeksOnHand(input)
  return {
    weeksOnHand,
    stockAtArrival: input.stockAtArrival,
    stockOutDate: coverEndStockOutDate(input, input.arrivalWeek, weeksOnHand),
    stockoutBeforeArrival: isStockoutBeforeArrival(
      input.stockoutWeeks,
      input.arrivalWeek,
      input.forecastInPeriod,
    ),
  }
}

/** The parts of a stock-out simulation input that an added-demand pool has to add up. */
type PoolableStockOutDetail = {
  skuId: string
  warehouseId: string
  forecasts: number[]
  stockLevel: number | null
  incomingOrders: { weekDiff: number; quantity: number }[] | null
  forecastedReturns: { weekDiff: number; quantity: number }[] | null
  salesWeekToDate?: number | null
}

/**
 * Merge each primary warehouse's added-demand warehouses into its own simulation input, so the
 * stock-out simulation runs on the balance the order is actually placed against.
 *
 * A PO with added demand is a single order into the primary warehouse that has to cover the demand
 * of the satellites as well: the page asks for suggestions across `[primary, ...addedDemand]` and
 * displays their demand summed, but the simulation runs per (sku, warehouse). Simulating the
 * primary alone projects a runway against a fraction of the demand it must cover and cannot
 * reconcile with the demand shown beside it.
 *
 * The whole balance is pooled, not just demand: stock, incoming orders and forecasted returns of
 * the satellites serve their own demand first, so pooling demand alone would understate cover
 * (a satellite with stock for the whole period would still shorten the primary's runway).
 *
 * Details for warehouses without added demand are returned untouched, and a mapping that adds
 * nothing returns the input array itself so callers' memos stay referentially stable.
 */
export function poolAddedDemandWarehouses<T extends PoolableStockOutDetail>(
  details: T[],
  addedDemandFor: (primaryWarehouseId: string) => string[],
): T[] {
  const hasPool = details.some((detail) => addedDemandFor(detail.warehouseId).length > 0)
  if (!hasPool) return details

  const byKey = new Map(details.map((detail) => [`${detail.skuId}:${detail.warehouseId}`, detail]))
  return details.map((detail) => {
    const sources = filterDefined(
      addedDemandFor(detail.warehouseId).map((warehouseId) =>
        byKey.get(`${detail.skuId}:${warehouseId}`),
      ),
    )
    if (!sources.length) return detail

    const curves = [detail.forecasts, ...sources.map((s) => s.forecasts)].filter((c) => c.length)
    const salesKnown =
      detail.salesWeekToDate != null || sources.some((s) => s.salesWeekToDate != null)
    return {
      ...detail,
      // Cumulative curves: pad the short ones with their last value before summing.
      forecasts: curves.length > 1 ? padAndSumCumulativeArrays(curves) : detail.forecasts,
      stockLevel: sumBy([detail, ...sources], (s) => s.stockLevel ?? 0),
      incomingOrders: mergeOrders(
        detail.incomingOrders,
        sources.map((s) => s.incomingOrders),
      ),
      forecastedReturns: mergeOrders(
        detail.forecastedReturns,
        sources.map((s) => s.forecastedReturns),
      ),
      // Elapsed-week demand is netted against the pooled curve, so it has to be pooled too. Left
      // unknown when no warehouse in the pool reported sales, which keeps the forecast proration.
      salesWeekToDate: salesKnown
        ? sumBy([detail, ...sources], (s) => s.salesWeekToDate ?? 0)
        : detail.salesWeekToDate,
    }
  })
}

type WeekQuantity = { weekDiff: number; quantity: number }

/** One ascending list of arrivals; `null` only when no warehouse in the pool has any. */
function mergeOrders(own: WeekQuantity[] | null, added: (WeekQuantity[] | null)[]) {
  const merged = [...(own ?? [])]
  for (const orders of added) merged.push(...(orders ?? []))
  if (!merged.length) return own
  return sortByWeekDiff(merged)
}

export function calculateWeeksOnHandForMultipleDates(
  closingStocks: { date: Date; quantity: number }[],
  salesAndForecasts: { date: Date; quantity: number | null }[],
) {
  const weeksOnHand: { value: number; date: Date }[] = []

  closingStocks.forEach((closingStock) => {
    if (closingStock.quantity === null || closingStock.quantity === 0) {
      weeksOnHand.push({
        value: 0,
        date: closingStock.date,
      })
      return
    }

    let stockRemaining = closingStock.quantity
    let weeksCovered = 0
    const filteredArray = salesAndForecasts.filter(
      (data) => !isSameDay(data.date, closingStock.date) && isAfter(data.date, closingStock.date),
    )

    if (filteredArray.length === 0 && stockRemaining > 0) {
      weeksCovered = 52
    }

    for (let i = 0; i < filteredArray.length; i++) {
      if (weeksCovered === 52 || stockRemaining <= 0) {
        break
      }

      const data = filteredArray[i]!
      const quantity = data.quantity ?? 0
      stockRemaining -= quantity

      if (i === filteredArray.length - 1 && stockRemaining > 0) {
        weeksCovered = 52
        break
      }

      if (stockRemaining <= 0) {
        // Calculate the fraction of the week that the remaining stock covers
        const fractionCovered = round((stockRemaining + quantity) / quantity, 2)

        weeksCovered += fractionCovered
        break
      }

      weeksCovered += 1
    }

    weeksOnHand.push({ date: closingStock.date, value: weeksCovered })
  })

  return weeksOnHand
}
